import React, { useState } from 'react';
import { JourneyStep, SkipType } from '../../types/journey';
import './StepSkipDialog.css';

interface StepSkipDialogProps {
  step: JourneyStep;
  isOpen: boolean;
  onClose: () => void;
  onSkip: (justification: string, skipType: SkipType) => Promise<void>;
  isLoading?: boolean;
}

export const StepSkipDialog: React.FC<StepSkipDialogProps> = ({
  step,
  isOpen,
  onClose,
  onSkip,
  isLoading = false
}) => {
  const [skipType, setSkipType] = useState<SkipType>('custom');
  const [justification, setJustification] = useState('');

  const handleSkip = async () => {
    if (!justification.trim()) {
      return;
    }

    try {
      await onSkip(justification.trim(), skipType);
      onClose();
      setJustification('');
      setSkipType('custom');
    } catch (error) {
      console.error('Error skipping step:', error);
    }
  };

  const handleJustificationChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJustification(e.target.value);
  };

  const getSkipTypeDescription = (type: SkipType): string => {
    switch (type) {
      case 'already_done':
        return 'I have already completed this step outside of the workflow';
      case 'not_applicable':
        return 'This step is not applicable to my situation or business';
      case 'custom':
        return 'Other reason (please explain below)';
    }
  };

  const getPlaceholderText = (type: SkipType): string => {
    switch (type) {
      case 'already_done':
        return 'Please describe what you have already done and when...';
      case 'not_applicable':
        return 'Please explain why this step is not applicable to your situation...';
      case 'custom':
        return 'Please explain your reason for skipping this step...';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="step-skip-dialog-overlay">
      <div className="step-skip-dialog">
        <div className="step-skip-dialog-header">
          <h2>Skip Step</h2>
          <button 
            className="step-skip-dialog-close"
            onClick={onClose}
            disabled={isLoading}
          >
            ×
          </button>
        </div>

        <div className="step-skip-dialog-content">
          <div className="step-skip-step-info">
            <h3>{step.title}</h3>
            <p>{step.description}</p>
          </div>

          <div className="step-skip-warning">
            <div className="step-skip-warning-icon">ℹ️</div>
            <div>
              <p><strong>Skipping this step will unlock the next step.</strong></p>
              <p>You can always come back to review the materials later.</p>
            </div>
          </div>

          <div className="step-skip-section">
            <h4>Reason for Skipping</h4>
            <div className="step-skip-types">
              {(['already_done', 'not_applicable', 'custom'] as SkipType[]).map((type) => (
                <label key={type} className="step-skip-type">
                  <input
                    type="radio"
                    name="skipType"
                    value={type}
                    checked={skipType === type}
                    onChange={(e) => setSkipType(e.target.value as SkipType)}
                    disabled={isLoading}
                  />
                  <div>
                    <strong>
                      {type === 'already_done' && 'Already Done'}
                      {type === 'not_applicable' && 'Not Applicable'}
                      {type === 'custom' && 'Other Reason'}
                    </strong>
                    <p>{getSkipTypeDescription(type)}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="step-skip-section">
            <label htmlFor="skip-justification">
              Justification <span className="step-skip-required">*</span>
            </label>
            <textarea
              id="skip-justification"
              value={justification}
              onChange={handleJustificationChange}
              placeholder={getPlaceholderText(skipType)}
              rows={4}
              disabled={isLoading}
              required
            />
            <div className="step-skip-char-count">
              {justification.length}/500 characters
            </div>
          </div>

          {step.dodCriteria && step.dodCriteria.length > 0 && (
            <div className="step-skip-section">
              <h4>What you'll miss by skipping:</h4>
              <ul className="step-skip-criteria">
                {step.dodCriteria.map((criteria, index) => (
                  <li key={index}>{criteria}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="step-skip-dialog-actions">
          <button
            className="step-skip-dialog-cancel"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className="step-skip-dialog-confirm"
            onClick={handleSkip}
            disabled={isLoading || !justification.trim() || justification.length > 500}
          >
            {isLoading ? 'Skipping...' : 'Skip Step'}
          </button>
        </div>
      </div>
    </div>
  );
};